import { useCallback, useEffect, useState } from 'react';

import { useDoBoxDropdownStore } from '../store';

export function useToggleItemSelectionDoBox<T extends { messageId: string }>(
  entryId: string,
  items: T[] = [],
) {
  const { saveDropdownSelectedItems, getDropdownSelectedItems } =
    useDoBoxDropdownStore();

  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => {
    const persistedItems = getDropdownSelectedItems(entryId);
    return new Set(persistedItems);
  });

  const saveSelection = useCallback(
    (newSelection: Set<string>) => {
      saveDropdownSelectedItems(entryId, Array.from(newSelection));
      setSelectedItems(newSelection);
    },
    [entryId, saveDropdownSelectedItems],
  );

  const toggleItemSelection = useCallback(
    (messageId: string) => {
      const newSelection = new Set(selectedItems);
      if (newSelection.has(messageId)) {
        newSelection.delete(messageId);
      } else {
        newSelection.add(messageId);
      }
      saveSelection(newSelection);
    },
    [selectedItems, saveSelection],
  );

  const toggleSelectAll = useCallback(() => {
    const itemsLength = items?.length ?? 0;
    if (selectedItems.size === itemsLength) {
      saveSelection(new Set());
    } else {
      const allMessageIds = new Set(items.map((item) => item.messageId));
      saveSelection(allMessageIds);
    }
  }, [items, selectedItems.size, saveSelection]);

  const clearSelection = useCallback(() => {
    saveSelection(new Set());
  }, [saveSelection]);

  useEffect(() => {
    const persistedItems = getDropdownSelectedItems(entryId);
    const persistedSet = new Set(persistedItems);
    if (
      persistedSet.size !== selectedItems.size ||
      !Array.from(persistedSet).every((item) => selectedItems.has(item))
    ) {
      setSelectedItems(persistedSet);
    }
  }, [entryId, getDropdownSelectedItems]);

  useEffect(() => {
    const currentMessageIds = new Set(items.map((item) => item.messageId));
    const itemsToRemove = Array.from(selectedItems).filter(
      (messageId) => !currentMessageIds.has(messageId),
    );

    if (itemsToRemove.length > 0) {
      const newSelection = new Set(selectedItems);
      itemsToRemove.forEach((messageId) => newSelection.delete(messageId));
      saveSelection(newSelection);
    }
  }, [items, selectedItems, saveSelection]);

  const isAllSelected =
    (items?.length ?? 0) > 0 && selectedItems.size === (items?.length ?? 0);
  const hasSelectedItems = selectedItems.size > 0;

  return {
    selectedItems,
    toggleItemSelection,
    toggleSelectAll,
    clearSelection,
    isAllSelected,
    hasSelectedItems,
  };
}
